import type { SendRawOptions } from '@/stores/connectionStore'

/**
 * The last request `sendRaw` actually dispatched, per conversation: text AND
 * structured actions (correctiveAction, orderExecutionAction, ...). "Tentar
 * novamente" manually replays the action, so a card click that failed is retried as
 * the same card click, not as a text turn the engine cannot understand.
 */
export interface LastTurnRequest {
  content: string
  options: Omit<SendRawOptions, 'inlineAttachments' | 'confirmationToken'>
  clientRequestId?: string
}

const lastByConversation = new Map<string, LastTurnRequest>()
const MAX_CONVERSATIONS = 20

export function recordLastTurnRequest(
  conversationId: string,
  content: string,
  options: SendRawOptions | undefined,
  clientRequestId?: string,
): LastTurnRequest {
  // Single-use token: replaying it would be refused, same as startTurnDeadline.
  const replayable: SendRawOptions = { ...options }
  delete replayable.confirmationToken
  delete replayable.inlineAttachments
  const request = { content, options: replayable, clientRequestId }
  // Refresh insertion order when this conversation sends another request.
  lastByConversation.delete(conversationId)
  lastByConversation.set(conversationId, request)
  if (lastByConversation.size > MAX_CONVERSATIONS) {
    const oldest = lastByConversation.keys().next().value
    if (oldest !== undefined) lastByConversation.delete(oldest)
  }
  return request
}

export function getLastTurnRequest(conversationId: string): LastTurnRequest | undefined {
  return lastByConversation.get(conversationId)
}

export function canAutoRetryLastTurnRequest(conversationId: string): boolean {
  const request = getLastTurnRequest(conversationId)
  // Only text/file references and voice-origin metadata are safe to replay
  // silently. Every other option carries an explicit intent/action.
  return (
    !request ||
    !Object.entries(request.options).some(
      ([key, value]) => value !== undefined && key !== 'fileRefs' && key !== 'commandSource',
    )
  )
}

export function clearLastTurnRequest(conversationId: string, expected: LastTurnRequest): void {
  // A late response must not erase the newer request in the same conversation.
  if (getLastTurnRequest(conversationId) === expected) lastByConversation.delete(conversationId)
}

export function clearLastTurnRequests(): void {
  lastByConversation.clear()
}
